import { createFileRoute } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { codeLabel, formatDateOnly } from "@alm/domain";
import { ResidentFaceCard } from "@/components/app/faces/resident-face-card";
import { orpc } from "@/lib/orpc";
import { NOT_SHOWN, withheldOf } from "@/lib/policy/withheld";
import { Field } from "../-components/field";
import { ageFrom } from "../-components/resident-dates";
import { AttendanceSummaryCard } from "./-components/stay/attendance-summary-card";
import { BillingCard } from "./-components/billing/billing-card";
import { EmergencyContactsCard } from "./-components/contacts/emergency-contacts-card";
import { HealthAttributes } from "./-components/chart/health-attributes";
import { InsuranceCard } from "./-components/billing/insurance-card";
import { PastAdmissionsCard } from "./-components/stay/past-admissions-card";
import { PharmaciesCard } from "./-components/care-network/pharmacies-card";
import { PoaCard } from "./-components/contacts/poa-card";
import { ProfileSection } from "./-components/chart/profile-section";

export const Route = createFileRoute("/_authenticated/_care/residents/$residentId/")({
  component: ProfileTab,
});

/** The profile tab in the order staff already work through it: who they are, what to know at the
 * bedside, the stay, then the people and policies around them — each editable where it is read. */
function ProfileTab() {
  const { residentId } = Route.useParams();
  const { data: resident } = useSuspenseQuery(
    orpc.care.residents.get.queryOptions({ input: { residentId } }),
  );
  const tz = resident.facilityTimezone ?? "UTC";
  const { facilityId } = resident;
  const hidden = withheldOf(resident);

  return (
    <div className="flex flex-col gap-4">
      <ProfileSection title="Personal information">
        <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
          <Field label="First name" value={resident.firstName} />
          <Field label="Last name" value={resident.lastName} />
          <Field label="Date of birth" value={formatDateOnly(resident.dateOfBirth, tz)} />
          <Field label="Age" value={`${ageFrom(resident.dateOfBirth)} years`} />
          <Field label="Gender" value={codeLabel(resident.gender)} />
          <Field label="Room number" value={resident.roomNumber} />
          <Field
            label="Social security number"
            value={hidden("ssn") ? NOT_SHOWN : resident.ssn || "Not provided"}
          />
          <Field
            label="Medicare number"
            value={hidden("medicareNumber") ? NOT_SHOWN : resident.medicareNumber || "Not provided"}
          />
        </dl>
      </ProfileSection>

      <HealthAttributes resident={resident} />

      {/* The admission date, length of stay and status are in the header's
          strip on every tab, so the profile no longer repeats them. */}
      <BillingCard resident={resident} />

      <AttendanceSummaryCard residentId={residentId} />
      <PharmaciesCard owner={{ kind: "resident", id: residentId }} facilityId={facilityId} />
      <InsuranceCard residentId={residentId} facilityId={facilityId} />
      <PoaCard residentId={residentId} facilityId={facilityId} />
      <EmergencyContactsCard residentId={residentId} facilityId={facilityId} />
      <ResidentFaceCard
        residentId={residentId}
        name={`${resident.firstName} ${resident.lastName}`}
        facilityId={facilityId}
        hasPhoto={Boolean(resident.photoUrl)}
      />
      <PastAdmissionsCard residentId={residentId} tz={tz} />
    </div>
  );
}
